// ── IndexedDB persistence for poem AI sessions ────────────────────────────────
// Storage priority: IndexedDB → localStorage → in-memory (EC-7).
//
// Object store layout:
//   database : "diwanApp"   version : 1
//   store    : "poemSessions"   keyPath : "poemId"
//
// getStorageMode() returns the backend currently in use:
//   'idb' — IndexedDB (survives reload, ~50 MB quota)
//   'ls'  — localStorage (survives reload, ~5 MB quota)
//   'mem' — in-memory only; data is lost on page reload (private browsing)

/** @import { PoemSession } from './poem-session-types.js' */

const DB_NAME    = 'diwanApp';
const DB_VERSION = 1;
const STORE      = 'poemSessions';
const LS_KEY     = 'poemSessions';

// ── IndexedDB ─────────────────────────────────────────────────────────────────

/** Cached open connection — reused across all calls in a session. */
let _db = null;

/** @returns {Promise<IDBDatabase>} */
function openDb() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = /** @type {IDBDatabase} */ (e.target.result);
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'poemId' });
      }
    };

    req.onsuccess = (e) => {
      _db = /** @type {IDBDatabase} */ (e.target.result);
      resolve(_db);
    };

    req.onerror = (e) => reject(/** @type {IDBRequest} */ (e.target).error);
  });
}

/** @param {string} poemId @returns {Promise<PoemSession|null>} */
async function idbGet(poemId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(poemId);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror  = (e) => reject(e.target.error);
  });
}

/** @param {PoemSession} session @returns {Promise<void>} */
async function idbPut(session) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readwrite').objectStore(STORE).put(session);
    req.onsuccess = () => resolve();
    req.onerror  = (e) => reject(e.target.error);
  });
}

/** @param {string} poemId @returns {Promise<void>} */
async function idbDelete(poemId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(poemId);
    req.onsuccess = () => resolve();
    req.onerror  = (e) => reject(e.target.error);
  });
}

// ── localStorage fallback ─────────────────────────────────────────────────────

/** @param {string} poemId @returns {PoemSession|null} */
function lsGet(poemId) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw)[poemId] ?? null) : null;
  } catch { return null; }
}

/** @param {PoemSession} session */
function lsPut(session) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[session.poemId] = session;
    localStorage.setItem(LS_KEY, JSON.stringify(all));
  } catch { /* quota exceeded — silent */ }
}

/** @param {string} poemId */
function lsDelete(poemId) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    const all = JSON.parse(raw);
    delete all[poemId];
    localStorage.setItem(LS_KEY, JSON.stringify(all));
  } catch { /* silent */ }
}

// ── In-memory fallback (EC-7) ─────────────────────────────────────────────────
// Used when both IDB and localStorage are unavailable (private browsing, some
// iOS WebViews). Data is per-page-session only — lost on reload.

const _memStore = new Map();

/** @param {string} poemId @returns {PoemSession|null} */
function memGet(poemId) { return _memStore.get(poemId) ?? null; }

/** @param {PoemSession} session */
function memPut(session) { _memStore.set(session.poemId, session); }

/** @param {string} poemId */
function memDelete(poemId) { _memStore.delete(poemId); }

// ── Storage mode detection ────────────────────────────────────────────────────

/** @type {'idb'|'ls'|'mem'|'unknown'} */
let _storageMode = 'unknown';

/**
 * Returns the active storage backend after the first read or write operation.
 * 'mem' means the session will not survive a page reload.
 *
 * @returns {'idb'|'ls'|'mem'|'unknown'}
 */
export function getStorageMode() { return _storageMode; }

function isLocalStorageAvailable() {
  try {
    const k = '__diwan_ls_probe__';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch { return false; }
}

// ── Public interface ──────────────────────────────────────────────────────────

/**
 * Reads the session for one poem. Returns null if none exists yet.
 * @param {string} poemId
 * @returns {Promise<PoemSession|null>}
 */
export async function dbGetSession(poemId) {
  try {
    const result = await idbGet(poemId);
    _storageMode = 'idb';
    return result;
  } catch {
    if (isLocalStorageAvailable()) {
      _storageMode = 'ls';
      return lsGet(poemId);
    }
    _storageMode = 'mem';
    return memGet(poemId);
  }
}

/**
 * Writes (create or replace) the session for one poem.
 * @param {PoemSession} session
 * @returns {Promise<void>}
 */
export async function dbPutSession(session) {
  try { await idbPut(session); _storageMode = 'idb'; }
  catch {
    if (isLocalStorageAvailable()) {
      lsPut(session);
      _storageMode = 'ls';
    } else {
      memPut(session);
      _storageMode = 'mem';
    }
  }
}

/**
 * Deletes the session for one poem entirely.
 * @param {string} poemId
 * @returns {Promise<void>}
 */
export async function dbDeleteSession(poemId) {
  try { await idbDelete(poemId); }
  catch {
    if (isLocalStorageAvailable()) { lsDelete(poemId); }
    else                           { memDelete(poemId); }
  }
}
